/**
 * @file The name an attachment reaches the disk under. Consumer:
 * `useAttachmentDownload`, on the lane that writes a file rather than returning
 * bytes. The name is all an operating system has to go on once the bytes have
 * left the browser, so a display name carrying no extension — which a panel
 * rename is free to leave — would otherwise save a file nothing can open.
 *
 * The rule appends and never replaces. A name the application can already read
 * a file type off is saved untouched, even when the declared type disagrees,
 * and that reading is `resolveAttachmentFileType` rather than a vocabulary of
 * this file's own: a second list would miss `jpeg`, `tiff` and `dwg` and save
 * `site photo.jpeg.jpg`, which no longer matches its attachment on re-upload.
 * A content type with no extension here leaves the name alone rather than
 * guessing one.
 */

import { resolveAttachmentFileType } from '../components/rendering/attachments/resolveAttachmentFileType';

/** The extension each declared content type is saved under. */
const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/json': 'json',
  'application/xml': 'xml',
  'application/zip': 'zip',
  'application/vnd.ms-outlook': 'msg',
  'message/rfc822': 'eml',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'text/xml': 'xml',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'image/tiff': 'tif',
  'image/heic': 'heic',
};

export const savedAttachmentFileName = (
  fileName: string,
  contentType: string | undefined,
): string => {
  const declared = (contentType ?? '').split(';')[0].trim().toLowerCase();
  const extension = EXTENSION_BY_CONTENT_TYPE[declared];
  if (!extension) return fileName;

  const lastDot = fileName.lastIndexOf('.');
  const trailing =
    lastDot > 0 ? fileName.slice(lastDot + 1).trim().toLowerCase() : '';
  const alreadyNamed =
    resolveAttachmentFileType(fileName, undefined) !== 'unknown'
    || trailing === extension;

  return alreadyNamed ? fileName : `${fileName}.${extension}`;
};
